import axios from 'axios';

// Production: use same origin (/api) so login works when frontend is on e.g. https://projects.nstargrp.com
const getApiUrl = () => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  if (typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
    return `${window.location.origin}/api`;
  }
  return 'http://localhost:3001/api';
};

const API_URL = getApiUrl();

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401: clear token and redirect to login (caller can still catch and show toast)
api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

export const authAPI = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),
  changePassword: (currentPassword: string, newPassword: string) =>
    api.post('/auth/change-password', { currentPassword, newPassword }),
  resetPassword: (token: string, newPassword: string) =>
    api.post('/auth/reset-password', { token, newPassword }),
  verifyEmail: (token: string) =>
    api.get('/auth/verify-email', { params: { token } }),
  resendVerification: (email: string) =>
    api.post('/auth/resend-verification', { email }),
};

export const usersAPI = {
  getAll: () => api.get('/users'),
  getById: (id: string) => api.get(`/users/${id}`),
  create: (name: string, email: string, password: string, role: 'masterAdmin' | 'admin' | 'endUser', organizations?: { organizationId: string; role: 'admin' | 'member' }[]) =>
    api.post('/users', { name, email, password, role, organizations }),
  update: (id: string, data: { name?: string; email?: string; password?: string; role?: 'masterAdmin' | 'admin' | 'endUser'; organizations?: { organizationId: string; role: 'admin' | 'member' }[] }) =>
    api.put(`/users/${id}`, data),
  delete: (id: string) => api.delete(`/users/${id}`),
  sendResetLink: (userId: string) => api.post(`/users/${userId}/send-reset-link`),
  sendVerificationLink: (userId: string) => api.post(`/users/${userId}/send-verification-link`),
};

export const organizationsAPI = {
  getAll: (includeAll?: boolean) => api.get(`/organizations${includeAll ? '?includeAll=true' : ''}`),
  getById: (id: string) => api.get(`/organizations/${id}`),
  getMembers: (id: string) => api.get(`/organizations/${id}/members`),
  create: (name: string, description?: string) =>
    api.post('/organizations', { name, description }),
  delete: (id: string) => api.delete(`/organizations/${id}`),
};

export const projectsAPI = {
  getByOrganization: (orgId: string, includeArchived?: boolean) => 
    api.get(`/projects/organization/${orgId}${includeArchived ? '?includeArchived=true' : ''}`),
  getById: (id: string) => api.get(`/projects/${id}`),
  create: (data: any) => api.post('/projects', data),
  update: (id: string, data: any) => api.put(`/projects/${id}`, data),
  updateDates: (id: string, data: { startDate?: string | null; endDate?: string | null; reason: string }) =>
    api.patch(`/projects/${id}/dates`, data),
  delete: (id: string) => api.delete(`/projects/${id}`),
  archive: (id: string, archived: boolean) => 
    api.patch(`/projects/${id}/archive`, { archived }),
  addColumn: (projectId: string, title: string, color?: string, setAsFinished?: boolean) =>
    api.post(`/projects/${projectId}/columns`, { title, color, setAsFinished }),
  setFinishedColumn: (projectId: string, columnId: string | null) =>
    api.patch(`/projects/${projectId}/finished-column`, { columnId }),
  reorderColumns: (projectId: string, columnIds: string[]) =>
    api.patch(`/projects/${projectId}/columns/reorder`, { columnIds }),
  deleteColumn: (projectId: string, columnId: string) =>
    api.delete(`/projects/${projectId}/columns/${columnId}`),
  addMember: (projectId: string, userId: string) =>
    api.post(`/projects/${projectId}/members`, { userId }),
  removeMember: (projectId: string, userId: string) =>
    api.delete(`/projects/${projectId}/members/${userId}`),
};

export const tasksAPI = {
  getByProject: (projectId: string) => api.get(`/tasks/project/${projectId}`),
  getById: (id: string) => api.get(`/tasks/${id}`),
  create: (data: any) => api.post('/tasks', data),
  update: (id: string, data: any) => api.put(`/tasks/${id}`, data),
  delete: (id: string) => api.delete(`/tasks/${id}`),
  updateStatus: (id: string, status: string, order: number) =>
    api.patch(`/tasks/${id}/status`, { status, order }),
  addComment: (taskId: string, text: string) =>
    api.post(`/tasks/${taskId}/comments`, { text }),
  deleteComment: (taskId: string, commentId: string) =>
    api.delete(`/tasks/${taskId}/comments/${commentId}`),
};

export default api;
